import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {BasicStatusTranslationService} from 'src/app/shared/translations';
import {environment} from '~environments/environment';
import {Attribute, CustomerAttribute} from '../models';

/**
 * Service for the administration of the club-overview attributes.
 */
@Injectable()
export class AttributeService {
  public API_URL: any = environment.envAPI;
  public API_URL_CLUB: any = `${environment.envAPI}/club-attributes`;
  public API_URL_CUSTOMER: any = `${environment.envAPI}/customer-attributes`;

  public editAttribute: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castEditAttribute: any = this.editAttribute.asObservable();

  public createAttribute: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  public castCreateAttribute: any = this.createAttribute.asObservable();

  public customerAttributes: BehaviorSubject<Array<CustomerAttribute>> = new BehaviorSubject<Array<CustomerAttribute>>(null);
  public castCustomerAttributes: any = this.customerAttributes.asObservable();

  public clubAttributes: BehaviorSubject<Array<Attribute>> = new BehaviorSubject<Array<Attribute>>(null);
  public castClubAttributes: any = this.clubAttributes.asObservable();

  public editAttributeModal: boolean = false;
  public createAttributeModal: boolean = false;
  public attributeSidebar: boolean = false;

  constructor(private http: HttpClient, private translations: BasicStatusTranslationService) {
  }

  /**
   * Get attributes by the club-overview id.
   */
  public getClubAttribute(id: number, customParams?: HttpParams): Observable<Array<Attribute>> {
    let params: HttpParams;
    if (customParams) {
      params = customParams;
    } else {
      params = new HttpParams()
        .set('order[status]', 'asc')
        .set('order[name]', 'asc')
        .set('itemsPerPage', '100')
        .set('status[]', 'inactive')
        .append('status[]', 'active');
    }

    return this.http.get(`${this.API_URL}/clubs/${id}/attributes`, {params}).pipe(
      map((response: any) => {
        const attributes: Array<Attribute> = [];
        response['hydra:member'].forEach(attribute => {
          attributes.push(new Attribute(attribute, this.translations));
        });
        return attributes;
      })
    );
  }

  /**
   * Get attributes endpoint.
   */
  public getClubAttributesEndpoint(id: number): any {
    const api = `${this.API_URL}/clubs/${id}/attributes`;

    const params: Array<any> = [
      {'order[status]': 'asc'},
      {'order[name]': 'asc'},
      {'status[]': 'active'},
      {'status[]': 'inactive'},
    ];

    return [api, params];
  }

  /**
   * Get attributes by the club-overview id.
   */
  public getAttribute(id: number): Observable<Attribute> {
    return this.http.get<Attribute>(`${this.API_URL_CLUB}/${id}`);
  }

  /**
   * Create a new attribute for the club-overview.
   */
  public createClubAttribute(attribute: any): any {
    return this.http.post(`${this.API_URL_CLUB}`, attribute).pipe(
      map(response => new Attribute(response, this.translations))
    );
  }

  /**
   * Update an attribute for a club-overview.
   */
  public updateClubAttribute(attribute: any, attributeID: any): Observable<Attribute> {
    return this.http.patch<Attribute>(`${this.API_URL_CLUB}/${attributeID}`, attribute).pipe(
      map((response: any) => new Attribute(response, this.translations))
    );
  }

  /**
   * Delete an attribute for a club-overview.
   */
  public deleteClubAttribute(attributeID: any): Observable<Attribute> {
    return this.http.delete<Attribute>(`${this.API_URL_CLUB}/${attributeID}`);
  }

  /**
   * Get attributes by the club-overview id.
   */
  public getCustomerAttribute(id: number): Observable<Array<CustomerAttribute>> {
    return this.http.get(`${this.API_URL}/customers/${id}/attributes`).pipe(
      map((response: any) => {
        const attributes: Array<CustomerAttribute> = [];
        response['hydra:member'].forEach(attribute => {
          attributes.push(new CustomerAttribute(attribute, this.translations));
        });
        return attributes;
      })
    );
  }

  /**
   * Create a new attribute for the club-overview.
   */
  public createCustomerAttribute(attribute: any): Observable<CustomerAttribute> {
    return this.http.post<CustomerAttribute>(`${this.API_URL_CUSTOMER}`, attribute).pipe(
      map((response: any) => new CustomerAttribute(response, this.translations))
    );
  }

  /**
   * Delete an attribute for a club-overview.
   */
  public deleteCustomerAttribute(attributeID: number): any {
    return this.http.delete(`${this.API_URL_CUSTOMER}/${attributeID}`);
  }

  /**
   * Adds values to the observer.
   */
  public announceCustomerAttributes(attributes: any): void {
    this.customerAttributes.next(attributes);
  }

  /**
   * Adds values to the observer.
   */
  public announceClubAttributes(attributes: any): void {
    this.clubAttributes.next(attributes);
  }

  /**
   * Adds values to the observer.
   */
  public announceEditAttribute(attribute: any): void {
    this.editAttribute.next(attribute);
  }

  /**
   * Adds values to the observer.
   */
  public announceCreateAttribute(attribute: any): void {
    this.createAttribute.next(attribute);
  }

  /**
   * Toggles the edit attribute modal
   */
  public toggleEditAttributeModal(value: boolean): void {
    this.attributeSidebar = value;
    this.editAttributeModal = value;
  }

  /**
   * Toggles the create attribute modal
   */
  public toggleCreateAttributeModal(value: boolean): void {
    this.attributeSidebar = value;
    this.createAttributeModal = value;
  }

  /**
   * Toggles all to false
   */
  public toggleAllModals(value: boolean): void {
    this.toggleCreateAttributeModal(value);
    this.toggleEditAttributeModal(value);
  }

  /**
   * Reset the value of attributes to null
   */
  public resetAttributes(): void {
    this.announceClubAttributes(null);
    this.announceCustomerAttributes(null);
    this.announceCreateAttribute(null);
    this.announceEditAttribute(null);
  }

  /**
   * Update a certain entry inside the attribute list
   */
  public addClubAttributeToList(attribute: Attribute): void {
    this.clubAttributes.getValue().push(attribute);
    this.sortAttributesByName(this.clubAttributes.getValue());
    this.sortAttributesByStatus(this.clubAttributes.getValue());
  }

  /**
   * Update a certain entry inside the attribute list
   */
  public updateClubAttributeToList(attribute: Attribute): void {
    const attributes = this.clubAttributes.getValue();
    const index = this.clubAttributes.getValue().findIndex(el => el.id === attribute.id);
    attributes[index] = attribute;
    this.sortAttributesByName(attributes);
    this.sortAttributesByStatus(attributes);
    this.announceClubAttributes(attributes);
  }

  /**
   * Delete a certain address from the address list using the id
   */
  public deleteAttributeFromList(attributeId: number): void {
    const index = this.clubAttributes.getValue().findIndex(el => el.id === attributeId);
    this.clubAttributes.getValue().splice(index, 1);
    this.sortAttributesByName(this.clubAttributes.getValue());
    this.sortAttributesByStatus(this.clubAttributes.getValue());
  }

  /**
   * Sorts the list of attributes by active first
   */
  public sortAttributesByStatus(attributes: Array<Attribute>): void {
    attributes.sort((x, y) => (x.status === y.status) ? 0 : x.status.localeCompare(y.status));
  }

  /**
   * Sorts the list of attributes by name
   */
  public sortAttributesByName(attributes: Array<Attribute>): void {
    attributes.sort((x, y) => x.name.localeCompare(y.name));
  }

}
